import { Badge, Button, Modal, Skeleton, Surface } from '@chopchop-se/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Ban, CheckCircle2, Printer } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { warehouseApi, warehouseKeys } from '../api';
import { printPort } from '../ports/print.port';
import { useAuthStore, userHasPermission } from '../../../auth/auth-store';
import { formatQty } from '../../../lib/format';
import { toastError, toastSuccess } from '../../../lib/notify';

/**
 * The bin detail drawer: what is in this bin, and the two flags a shift lead
 * actually changes — pick location and blocked. Both go through
 * `PATCH /warehouse/bins/:id`, which is a CommandBus command, so the change
 * lands in the bin's audit history.
 */
export function BinDrawer({ binId, onClose }: { binId: string | null; onClose: () => void }): ReactNode {
  const { t } = useTranslation(['warehouseCore', 'common']);
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const permissions = useAuthStore((s) => s.permissions);
  const canManage = userHasPermission(user, permissions, ['warehouse.locations.manage']);
  const canPrint = userHasPermission(user, permissions, ['warehouse.print']);

  const { data, isPending } = useQuery({
    queryKey: warehouseKeys.bin(binId ?? ''),
    queryFn: () => warehouseApi.getBin(binId!),
    enabled: Boolean(binId),
  });

  const update = useMutation({
    mutationFn: (patch: { isPickLocation?: boolean; isBlocked?: boolean }) =>
      warehouseApi.updateBin(binId!, patch),
    onSuccess: () => {
      toastSuccess(t('warehouseCore:bins.saved'));
      void queryClient.invalidateQueries({ queryKey: warehouseKeys.all });
    },
    onError: (error) => {
      toastError(error);
    },
  });

  const print = useMutation({
    mutationFn: () =>
      printPort.print({
        kind: 'bin_label',
        payload: {
          binCode: data!.code,
          areaName: data!.areaName,
          shelfCode: data!.shelfCode,
          isPickLocation: data!.isPickLocation,
        },
      }),
    onSuccess: () => {
      toastSuccess(t('warehouseCore:pallets.printQueued'));
    },
    onError: () => {
      toastError(new Error(t('warehouseCore:pallets.printUnavailable')));
    },
  });

  if (!binId) return null;

  return (
    <Modal
      open
      onClose={onClose}
      title={data ? t('warehouseCore:bins.detail', { code: data.code }) : t('warehouseCore:bins.title')}
      subtitle={data ? `${data.areaCode} · ${data.areaName}` : undefined}
      width={640}
    >
      {isPending || !data ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-4 w-40 rounded" />
          <Skeleton className="h-24 w-full rounded" />
        </div>
      ) : (
        <div className="flex flex-col gap-5">
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone="neutral">{t(`warehouseCore:bins.kinds.${data.kind}`)}</Badge>
            {data.isPickLocation ? <Badge tone="brand">{t('warehouseCore:bins.pickLocation')}</Badge> : null}
            {data.isBlocked ? <Badge tone="danger">{t('warehouseCore:bins.blocked')}</Badge> : null}
            {data.shelfCode ? (
              <span className="font-mono text-xs text-ink-muted">
                {t('warehouseCore:bins.shelf')} {data.shelfCode}
              </span>
            ) : null}
          </div>

          <section>
            <h3 className="mb-2 font-mono text-xs uppercase tracking-[0.14em] text-ink-muted">
              {t('warehouseCore:bins.contents')}
            </h3>
            {data.contents.length === 0 ? (
              <Surface variant="subtle" className="p-4 text-sm text-ink-muted">
                {t('warehouseCore:bins.noContents')}
              </Surface>
            ) : (
              <ul className="flex flex-col gap-2">
                {data.contents.map((line) => (
                  <li
                    key={line.stockId}
                    className="flex items-center justify-between gap-3 rounded-lg border border-surface-border px-3 py-2"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-sm text-ink">
                        <span className="font-mono text-xs text-ink-muted">{line.articleNo}</span>{' '}
                        {line.articleName}
                      </p>
                      <p className="truncate text-xs text-ink-muted">
                        {line.lotNo ? `${t('warehouseCore:stock.lot')} ${line.lotNo}` : null}
                        {line.bestBefore ? ` · ${line.bestBefore}` : null}
                        {line.lpn ? ` · ${line.lpn}` : null}
                      </p>
                    </div>
                    <span className="shrink-0 font-mono text-sm text-ink">
                      {formatQty(line.qtyBase, { unit: line.unitCode ?? undefined })}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <div className="flex flex-wrap gap-2">
            {canManage ? (
              <>
                <Button
                  variant="secondary"
                  size="sm"
                  icon={<CheckCircle2 />}
                  loading={update.isPending}
                  onClick={() => {
                    update.mutate({ isPickLocation: !data.isPickLocation });
                  }}
                >
                  {data.isPickLocation
                    ? t('warehouseCore:bins.unmarkPickLocation')
                    : t('warehouseCore:bins.markPickLocation')}
                </Button>
                <Button
                  variant={data.isBlocked ? 'secondary' : 'danger'}
                  size="sm"
                  icon={<Ban />}
                  loading={update.isPending}
                  onClick={() => {
                    update.mutate({ isBlocked: !data.isBlocked });
                  }}
                >
                  {data.isBlocked ? t('warehouseCore:bins.unblock') : t('warehouseCore:bins.block')}
                </Button>
              </>
            ) : null}
            {canPrint ? (
              <Button
                variant="ghost"
                size="sm"
                icon={<Printer />}
                disabled={!printPort.available}
                title={printPort.available ? undefined : t('warehouseCore:pallets.printUnavailable')}
                loading={print.isPending}
                onClick={() => {
                  print.mutate();
                }}
              >
                {t('warehouseCore:bins.printLabel')}
              </Button>
            ) : null}
            <Button variant="ghost" size="sm" className="ml-auto" onClick={onClose}>
              {t('common:actions.close')}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
